import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import Button from 'portal-modules/common/components/Button'
import insertId from 'portal-modules/common/utils/insert-id'
import { withRouter } from 'react-router-dom'
import { ModalControl } from '@entrata/portals-common/lib/components'
import { RENEWAL } from 'portal-modules/renewals/constants/urls'
import { RENEWALS_COLUMNS } from 'portal-modules/renewals/constants/column_keys'
import { AlertTriangle, Info } from '@entrata/portals-common/lib/icons'
import {
  OtherChargesModal,
  ChargeUpdatesModal,
} from 'portal-modules/common/components/modals'
import styled from '@emotion/styled'

import { StyledLedgerTable, StyledTr, StyledTd, StyledTdVerticallyTop } from 'portal-modules/renewals/components/atoms'
import { MonthToMonthOfferModal } from './modals'
import { connect } from 'react-redux'
import getSortedLeaseTerms from 'portal-modules/renewals/utils/getSortedLeaseTerms'
import { localStorage as local } from '@entrata/utils'
import { StyledClickableSpan, StyledChargesInfoIcon, StyledTotalHeader } from 'portal-modules/renewals/components/StyledComponents'

const {
  LEASE_NAME,
  TERM,
  RENT,
  PERCENT_CHANGE,
  AMOUNT_CHANGE,
  OTHER_CHARGES,
  NEW_TOTAL,
  INCENTIVES,
} = RENEWALS_COLUMNS

const StyledAlertDiv = styled('div')`
  color: ${({ theme }) => theme.error.base};
  display: flex;
  margin: 0;
`

const StyledAlertSpan = styled('span')`
  font-size: 13px;
  font-style: italic;
  margin-left: 8px;
  vertical-align: top;
`

export const rateFrequency = rate_frequency =>
  rate_frequency ? rate_frequency.replace('&#47;', '/') : ''

const WarningDiv = ({ leaseEndDate, isLeaseAndRentWordReplaced }) => {
  const leaseTerm = isLeaseAndRentWordReplaced ? 'housing agreement' : 'lease'
  return (
    <StyledAlertDiv>
      {leaseEndDate && (
        <Fragment>
          <AlertTriangle size={16} />
          <StyledAlertSpan>
            {__(
              'Current {%s, 2} expires on {%s, 0}, {%s, 1} If no renewal offer is accepted upon {%s, 2} expiration your {%s, 2} will default to month to month with the below amounts.',
              [
                i18nUtils.datetime(leaseEndDate, 'MMM D'),
                i18nUtils.datetime(leaseEndDate, 'YYYY'),
                leaseTerm
              ],
            )}
          </StyledAlertSpan>
        </Fragment>
      )}
    </StyledAlertDiv>
  )
}

WarningDiv.propTypes = {
  leaseEndDate: PropTypes.string.isRequired,
}


const formattedOfferData = data =>
  data.map(details => {
    const {
      changed_amount = null,
      incentive,
      lease_term_name,
      lease_term_length,
      new_total,
      other_charges,
      base_rent_charges,
      percent_change,
      rent,
      total_rent_charge_amount,
      total_other_charge_amount,
      total_other_charge_tax,
      total_rent_tax_amount,
      total_concession_charge_tax,
      tax_amount,
      concession,
      quoteId,
      current_base_rent,
      total_incentive_tax_amount,
      custom_charges,
      total_custom_charge_amount,
      total_custom_charge_tax,
    } = details

    const lease_term_length_with_months =
      lease_term_length != '' ? lease_term_length + ' months' : ''

    return {
      incentives:
        incentive &&
        Object.keys(incentive)?.reduce((arr, key) => {
          return [
            ...arr,
            {
              ...incentive[key],
              id: key,
            },
          ]
        }, []),
      concession:
        concession &&
        Object.keys(concession)?.reduce((arr, key) => {
          return [
            ...arr,
            {
              ...concession[key],
              id: key,
            },
          ]
        }, []),
      leaseId: quoteId,
      leaseName: lease_term_name,
      term: lease_term_length_with_months,
      newTotal: new_total,
      otherCharges:
        other_charges &&
        Object.keys(other_charges)?.reduce((arr, key) => {
          return [
            ...arr,
            {
              ...other_charges[key],
              id: key,
            },
          ]
        }, []),
      baseRentCharges:
          base_rent_charges &&
          Object.keys(base_rent_charges)?.reduce((arr, key) => {
            return [
              ...arr,
              {
                ...base_rent_charges[key],
                id: key,
              },
            ]
          }, []),
      percentChange: percent_change,
      amountChange: changed_amount,
      rent: rent || ( typeof total_rent_charge_amount != 'undefined' && total_rent_charge_amount ),
      totalOtherCharge: total_other_charge_amount,
      totalOtherChargeTax: total_other_charge_tax,
      totalRentTaxAmount: total_rent_tax_amount,
      totalConcessionChargeTax: total_concession_charge_tax,
      taxAmount: tax_amount,
      current_base_rent,
      total_incentive_tax_amount,
      billingSegments: details.hasOwnProperty('billing_segments')?details.billing_segments:[],
      customCharges: custom_charges || [],
      totalCustomChargeAmount: total_custom_charge_amount || 0,
      totalCustomChargeTax: total_custom_charge_tax || 0,
    }
  })

class RenewalOfferTable extends Component {
  handleSelectOffer = quoteTermId => {
    local.remove('installment-id')
    const { history, offerId, offerDetails:{quote_lease_terms} } = this.props
    Object.keys(quote_lease_terms[quoteTermId]?.installment_plans || {}).length ? history.push(insertId(RENEWAL.SELECT_OFFER.INSTALLMENT_PLANS, quoteTermId, { offerId }))
    : history.push(insertId(RENEWAL.SELECT_OFFER.ROOT, quoteTermId, { offerId }))
  }

  formatLeaseQuotes = leaseTerms =>
    Object.keys(leaseTerms ?? {}).reduce((arr, key) => {
          return [
            ...arr,
            {
              ...leaseTerms[key],
              quoteId: key,
            },
          ]
        }, [])

  render() {
    const {
      monthToMonth,
      offerDetails,
      showSelectButton,
      leaseEndDate,
      history,
      rate_frequency,
      taxLabel,
      includeTaxesInAdvertisedPricing,
      isRenewalTransfer,
      allowMToMSelection,
      isLeaseAndRentWordReplaced,
      totalMonthlyLeasingPricingLabel,
    } = this.props

    const { offer_expires_on, quote_lease_terms, sort_offer_by } = offerDetails
    const rentReplacedText = isLeaseAndRentWordReplaced ? __('Housing Fee') : __('Rent')
    const leaseReplacedText = isLeaseAndRentWordReplaced ? __('Housing Agreements') : __('Leases')

    const allColumns = [
      { id: LEASE_NAME, name: leaseReplacedText },
      { id: TERM, name: __('Term') },
      { id: RENT, name: rentReplacedText },
      { id: PERCENT_CHANGE, name: __('Change') },
      { id: AMOUNT_CHANGE, name: __('Change') },
      { id: OTHER_CHARGES, name: __('Charges') },
      { id: NEW_TOTAL, name: __('New Total') },
      { id: INCENTIVES, name: __('Incentives') },
    ]

    let sortedLeaseTerms =
      quote_lease_terms && this.formatLeaseQuotes(quote_lease_terms)

    sortedLeaseTerms =
      (sortedLeaseTerms &&
        getSortedLeaseTerms(sortedLeaseTerms, sort_offer_by)) ||
      []

    const leases = sortedLeaseTerms && formattedOfferData(sortedLeaseTerms)
    const monthToMonthData = monthToMonth && formattedOfferData([monthToMonth])
    const groups = [{ rows: leases }]
    if (monthToMonthData && offer_expires_on !== 'Expired') {
      groups.push({
        noBottomBorder: true,
        rows: monthToMonthData,
        title: (
          <WarningDiv
            leaseEndDate={leaseEndDate}
            isLeaseAndRentWordReplaced={isLeaseAndRentWordReplaced}
          />
        ),
      })
    }

    const columns = allColumns.filter(column => !(column.id === TERM))
    let isChangeExists = false
    const activeColumns = columns
      .filter(({ id }) =>
        leases.find(lease =>
          lease[id]
            ? lease[id]
            : monthToMonthData?.length &&
              monthToMonthData?.[0]?.hasOwnProperty(id)
            ? monthToMonthData[0][id]
            : '',
        ),
      )
      .filter(col => {
        if (col.id == PERCENT_CHANGE && !isChangeExists) {
          isChangeExists = true
          return col
        }
        if (col.id == AMOUNT_CHANGE && isChangeExists) {
          return false
        }
        return col
      })

    let hasIncentives = false
    leases?.forEach(({ incentives }) => {
      if (typeof incentives == 'object') {
        hasIncentives = true
      }
    })

    // Check if any lease terms have custom_charges
    let hasCustomCharges = false
    if (leases) {
      hasCustomCharges = leases.some(lease =>
        lease.customCharges && Array.isArray(lease.customCharges) && lease.customCharges.length > 0
      )
    }
    if (monthToMonthData?.length) {
      hasCustomCharges = hasCustomCharges || monthToMonthData.some(lease =>
        lease.customCharges && Array.isArray(lease.customCharges) && lease.customCharges.length > 0
      )
    }

    // Create custom header for Charges column with tooltip if custom_charges exist
    const chargesColumnHeader = hasCustomCharges ? (
      <>
        {__('Charges')}
        <StyledChargesInfoIcon>
          <Info size={12} />
        </StyledChargesInfoIcon>
      </>
    ) : __('Charges')

    // Create custom header for New Total column if total_monthly_leasing_pricing_label exists
    const newTotalColumnHeader = totalMonthlyLeasingPricingLabel ? (
      <StyledTotalHeader>
        {totalMonthlyLeasingPricingLabel}
      </StyledTotalHeader>
    ) : __('New Total')

    // Update the activeColumns to use the custom headers
    const updatedActiveColumns = activeColumns.map(column => {
      if (column.id === OTHER_CHARGES) {
        return { ...column, name: chargesColumnHeader }
      }
      if (column.id === NEW_TOTAL) {
        return { ...column, name: newTotalColumnHeader }
      }
      return column
    })

    return (
      <StyledLedgerTable
        columns={[
          ...updatedActiveColumns,
          showSelectButton && { id: 'button', align: 'right', name: '' },
        ]}
        groups={groups}
        renderRow={({
          getColProps,
          incentives,
          leaseId,
          leaseName,
          newTotal,
          otherCharges,
          baseRentCharges,
          percentChange,
          rent,
          totalOtherCharge,
          totalOtherChargeTax,
          amountChange,
          totalRentTaxAmount,
          totalConcessionChargeTax,
          taxAmount,
          concession,
          current_base_rent,
          total_incentive_tax_amount,
          customCharges,
          totalCustomChargeAmount,
          totalCustomChargeTax,
        }) => {
          const charges_amount =
            (includeTaxesInAdvertisedPricing && totalOtherCharge) ||
            totalOtherCharge + totalOtherChargeTax
          let hasOtherCharges = groups.map(group => {
            group.rows.filter(
              element => element?.otherCharges?.length,
            )
          })
          let segmentedResponse = groups.reduce((arr,group) => {
            if(Array.isArray(group.rows)){
              const billingSegment = group.rows.reduce((acc,leaseTerm)=>{
                if(leaseId === leaseTerm.leaseId && leaseTerm.hasOwnProperty('billingSegments')){
                  return { ...acc, ...leaseTerm.billingSegments}
                }
                return acc
              },{})
              return{...arr, ...billingSegment}
            }
            return arr
          },{})

          let showChangedAmount
          let billingSegments = (segmentedResponse && typeof segmentedResponse === 'object' && Object.keys(segmentedResponse)?.length > 0)?(
              Object.keys(segmentedResponse).map((segmentId, index) => {
                  const segment = segmentedResponse[segmentId]
                  const rent = segment.rent || segment.rent === 0
                  const billingSegmentName = segment.billing_segment_name
                  const totalOtherChargeAmount = segment.show_charge_amount && segment.total_other_charge_amount ? i18nUtils.currency(
                     ( includeTaxesInAdvertisedPricing
                     ? segment?.total_other_charge_amount
                     : segment?.total_other_charge_amount + ( segment?.total_other_charge_tax || 0)),
                     'p:2'
                    ) : ''
                  let changedAmountPrefix
                  let changedAmount
                  let percentChangePrefix
                  let percentChange

                  if(segment.show_changed_amount && ( segment.changed_amount || segment.percent_change ) ) {
                      if(!showChangedAmount){
                        showChangedAmount = segment.show_changed_amount
                      }
                      if( segment.changed_amount ){
                        changedAmountPrefix = segment.changed_amount > 0 ? '+' : ''
                        changedAmount = i18nUtils.currency(segment.changed_amount, 'p:2')
                      }
                      if( segment.percent_change ){
                        percentChangePrefix = segment.percent_change > 0 ? ' (+' : '('
                        percentChange = __('{%s, 0}%)', [segment.percent_change])
                      }
                  }

                  return {
                    rent: i18nUtils.currency(rent,'p:2'),
                    billingSegmentName: billingSegmentName && billingSegmentName !== '' ? __('{%s,0}',[billingSegmentName]):'',
                    changedAmountPrefix: changedAmountPrefix,
                    changedAmount: changedAmount,
                    percentChangePrefix: percentChangePrefix,
                    percentChange: percentChange,
                    totalOtherChargeAmount: totalOtherChargeAmount,
                  }
              })
          ):[]

          return (
            <StyledTr key={leaseId}>
              {leaseName && (
                segmentedResponse && typeof segmentedResponse === 'object' && Object.keys(segmentedResponse).length > 0 ?(
                  <StyledTdVerticallyTop {...getColProps(LEASE_NAME)}>{leaseName}</StyledTdVerticallyTop>
                ):(
                  <StyledTd {...getColProps(LEASE_NAME)}>{leaseName}</StyledTd>
                )
              )}
              <StyledTd {...getColProps(RENT)}>
                {
                  billingSegments.length > 0 ? (
                    billingSegments.map((leaseTerm,index)=>{
                      return leaseTerm.rent ? (
                          <>
                            {leaseTerm.billingSegmentName}
                            <br/>
                            {leaseTerm.rent}
                            {rateFrequency(rate_frequency)}
                            <br/>
                            <br/>
                          </>
                        ):'-'
                    })
                  ) :(
                      (rent || rent == 0) && (
                        <>
                          {i18nUtils.currency(rent, 'p:2')}
                          {rateFrequency(rate_frequency)}
                        </>
                      )
                  )
                }
              </StyledTd>
              {showChangedAmount && billingSegments.length > 0 ? (
                <StyledTdVerticallyTop {...getColProps('amountChange')}>
                    {billingSegments.map((leaseTerm,index)=>{
                      return leaseTerm.changedAmount ? (
                          <>
                            {leaseTerm.changedAmountPrefix}
                            {leaseTerm.changedAmount}
                            <br/>
                            {leaseTerm.percentChangePrefix}
                            {leaseTerm.percentChange}
                            <br/>
                            <br/>
                          </>
                        ):''
                    })}
                </StyledTdVerticallyTop>
              ) :
                (amountChange || percentChange ? (
                      <StyledTd
                      {...getColProps('amountChange')}>
                        {amountChange && amountChange > 0 ? '+' : ''}
                        {amountChange && i18nUtils.currency(amountChange, 'p:2')}
                        {amountChange && percentChange && <br />}
                        {percentChange ? (percentChange > 0 ? ' (+' : '(') : ''}
                        {percentChange ? __('{%s, 0}%)', [percentChange]) : ''}
                      </StyledTd>
                    ) : (
                        activeColumns.find(
                          element =>
                            element['id'] == PERCENT_CHANGE ||
                            element['id'] == AMOUNT_CHANGE,
                        ) && <StyledTd {...getColProps(PERCENT_CHANGE)}>-</StyledTd>
                      )
                )
              }
              {billingSegments.length > 0 ? (
                <StyledTdVerticallyTop {...getColProps(OTHER_CHARGES)}>
                    {billingSegments.map((leaseTerm,index)=>{
                      return leaseTerm.totalOtherChargeAmount ? (
                          <>
                            <span>{leaseTerm.totalOtherChargeAmount}</span>
                            <br/>
                            <br/>
                            <br/>
                          </>
                        ):''
                    })}
                </StyledTdVerticallyTop>
              ) :
              (!!hasOtherCharges.length && otherCharges ? (
                  <StyledTd {...getColProps(OTHER_CHARGES)}>
                    {charges_amount && (
                      <span>{i18nUtils.currency(charges_amount, 'p:2')}</span>
                    )}
                  </StyledTd>
                ) : (
                  activeColumns.find(
                    element => element['id'] == OTHER_CHARGES,
                  ) && <StyledTd {...getColProps(OTHER_CHARGES)}>-</StyledTd>
              ))}
              {
                segmentedResponse && typeof segmentedResponse === 'object' && Object.keys(segmentedResponse).length > 0 ?(
                  <StyledTd {...getColProps(NEW_TOTAL)}>
                    {Object.keys(segmentedResponse).map((segmentId,index)=>{
                        return (segmentedResponse[segmentId].new_total || segmentedResponse[segmentId].new_total === 0) ? (
                          <ModalControl>
                            {({ onClick, ref, ...rest }) => (
                              <Fragment>
                                {(totalOtherCharge || segmentedResponse[segmentId].rent) && (
                                  <>
                                    <span>
                                      {segmentedResponse[segmentId].billing_segment_name && segmentedResponse[segmentId].billing_segment_name !== '' ?
                                        <>
                                          {__( '{%s,0}',[segmentedResponse[segmentId].billing_segment_name])}
                                          <br/>
                                        </>
                                      :''}
                                    </span>
                                    <StyledClickableSpan onClick={onClick} ref={ref}>
                                      {i18nUtils.currency(segmentedResponse[segmentId].new_total, 'p:2')}
                                      {rateFrequency(rate_frequency)}
                                    </StyledClickableSpan>
                                  </>
                                )}

                                <ChargeUpdatesModal
                                  rate_frequency={rate_frequency}
                                  taxLabel={taxLabel}
                                  {...segmentedResponse[segmentId]}
                                  lease_name_MTM={leaseName === 'Month To Month'}
                                  custom_charges={customCharges}
                                  total_custom_charge_amount={totalCustomChargeAmount}
                                  total_custom_charge_tax={totalCustomChargeTax}
                                  {...rest}
                                />
                                {<br />}
                                {<br />}
                              </Fragment>
                            )}
                          </ModalControl>
                        ) : ''
                    })}
                  </StyledTd>
                ):
                (newTotal || newTotal === 0 ? (
                    <StyledTd {...getColProps(NEW_TOTAL)}>
                      <ModalControl>
                        {({ onClick, ref, ...rest }) => (
                          <Fragment>
                            {(totalOtherCharge || rent) && (
                              <StyledClickableSpan onClick={onClick} ref={ref}>
                                {i18nUtils.currency(newTotal, 'p:2')}
                                {rateFrequency(rate_frequency)}
                              </StyledClickableSpan>
                            )}

                            <ChargeUpdatesModal
                              total_other_charge_amount={totalOtherCharge}
                              other_charges={otherCharges}
                              base_rent_charges={baseRentCharges}
                              rate_frequency={rate_frequency}
                              taxLabel={taxLabel}
                              total_other_charge_tax={totalOtherChargeTax}
                              rent={rent}
                              total_rent_tax_amount={totalRentTaxAmount}
                              total_concession_charge_tax={totalConcessionChargeTax}
                              percent_change={percentChange}
                              tax_amount={taxAmount}
                              new_total={newTotal}
                              changed_amount={amountChange}
                              concession={concession}
                              current_base_rent={current_base_rent}
                              lease_name_MTM={leaseName === 'Month To Month'}
                              custom_charges={customCharges}
                              total_custom_charge_amount={totalCustomChargeAmount}
                              total_custom_charge_tax={totalCustomChargeTax}
                              {...rest}
                            />
                          </Fragment>
                        )}
                      </ModalControl>
                    </StyledTd>
                  ) : (
                    <StyledTd />
              ))}
              {(hasIncentives || incentives) && (
              showChangedAmount && billingSegments.length > 0 ? (
                <StyledTdVerticallyTop {...getColProps(INCENTIVES)}>
                  <ModalControl>
                      {({ onClick, ref, ...rest }) => (
                        <Fragment>
                          <StyledClickableSpan
                            onClick={incentives && incentives.length && onClick}
                            ref={ref}
                          >
                            {(incentives && incentives.length) || '-'}
                          </StyledClickableSpan>
                          {incentives && (
                            <OtherChargesModal
                              other_charges={incentives}
                              total_incentive_tax_amount={
                                total_incentive_tax_amount
                              }
                              rate_frequency={rate_frequency}
                              modalTitle={__('Incentives')}
                              {...rest}
                            />
                          )}
                        </Fragment>
                      )}
                    </ModalControl>
                  </StyledTdVerticallyTop>
              )
              :
              (
                <StyledTd {...getColProps(INCENTIVES)}>
                  <ModalControl>
                    {({ onClick, ref, ...rest }) => (
                      <Fragment>
                        <StyledClickableSpan
                          onClick={incentives && incentives.length && onClick}
                          ref={ref}
                        >
                          {(incentives && incentives.length) || '-'}
                        </StyledClickableSpan>
                        {incentives && (
                          <OtherChargesModal
                            other_charges={incentives}
                            total_incentive_tax_amount={
                              total_incentive_tax_amount
                            }
                            rate_frequency={rate_frequency}
                            modalTitle={__('Incentives')}
                            {...rest}
                          />
                        )}
                      </Fragment>
                    )}
                  </ModalControl>
                </StyledTd>
              ))}
              {showSelectButton ? (
                leaseName !== 'Month To Month' ? (
                  <StyledTdVerticallyTop {...getColProps('button')}>
                    <Button
                      appearance='secondary'
                      id={leaseId}
                      onClick={() => this.handleSelectOffer(leaseId)}
                      slim
                    >
                      {__('Select')}
                    </Button>
                  </StyledTdVerticallyTop>
                ) : (
                  !isRenewalTransfer &&
                  !!allowMToMSelection && (
                    <StyledTd {...getColProps('button')}>
                      <ModalControl>
                        {({ onClick, ref, ...props }) => (
                          <Fragment>
                            <Button
                              appearance='secondary'
                              onClick={onClick}
                              ref={ref}
                              slim
                            >
                              {__('Select')}
                            </Button>
                            <MonthToMonthOfferModal
                              {...props}
                              history={history}
                            />
                          </Fragment>
                        )}
                      </ModalControl>
                    </StyledTd>
                  )
                )
              ) : (
                <StyledTd />
              )}
            </StyledTr>
          )
        }}
      />
    )
  }
}

RenewalOfferTable.propTypes = {
  history: PropTypes.object.isRequired,
  monthToMonth: PropTypes.object,
  offerDetails: PropTypes.object.isRequired,
  offerId: PropTypes.number.isRequired,
  showSelectButton: PropTypes.bool,
  leaseEndDate: PropTypes.string,
  rate_frequency: PropTypes.string,
  taxLabel: PropTypes.string,
  includeTaxesInAdvertisedPricing: PropTypes.bool,
  isStudentProperty: PropTypes.bool,
  isRenewalTransfer: PropTypes.bool,
  allowMToMSelection: PropTypes.bool,
  isLeaseAndRentWordReplaced: PropTypes.bool,
  totalMonthlyLeasingPricingLabel: PropTypes.string,
}

RenewalOfferTable.defaultProps = {
  showSelectButton: true,
}

export default connect(
  ({
    settings: { renewal },
    productPermissions: { replace_lease_and_rent },
  }) => ({
    rate_frequency: (renewal && renewal.rate_frequency) || '',
    taxLabel: (renewal && renewal.tax_label) || '',
    includeTaxesInAdvertisedPricing:
      renewal && renewal.include_taxes_in_advertised_pricing,
    isStudentProperty: !!renewal?.is_student_property,
    isRenewalTransfer: !!renewal?.is_renewal_transfer,
    allowMToMSelection: renewal?.allow_resident_to_select_mtom,
    isLeaseAndRentWordReplaced: replace_lease_and_rent || false,
    totalMonthlyLeasingPricingLabel: renewal?.total_monthly_leasing_pricing_label,
  }),
)(withRouter(RenewalOfferTable))
